// Backend-for-Frontend (BFF) auth for the SPA.
//
// Auth tokens are NEVER stored in the browser. The SPA holds only an opaque
// HttpOnly session cookie (bb_session) issued by the backend. Identity for UI
// role gating is bootstrapped from GET /auth/me (never tokens). Login/logout go
// through the backend BFF endpoints. A non-HttpOnly CSRF cookie (bb_csrf) is
// echoed on mutating API calls via the X-CSRF-Token header (double-submit).

import {
    IT_ROLE,
    NON_GPSC_USER_ROLE,
    ECR_AGENT_USER_ROLE,
    ADMIN_ROLES,
    ALL_VALID_ROLES,
    SUPPLYCHAIN_USER_ROLE,
    DOMAIN_4_USER_ROLE
} from "./constants";

const BACKEND_URI = "";

export interface AuthClaims {
    roles?: string[];
    name?: string;
    [key: string]: any;
}

// Lightweight replacement for the previous MSAL AccountInfo shape. Only the
// identity claims (roles, name, etc.) surfaced by /auth/me are represented.
export interface AuthAccount {
    idTokenClaims: AuthClaims;
}

interface AuthSetup {
    useLogin: boolean;
    requireAccessControl: boolean;
    authMode?: string;
    loginUrl: string;
    logoutUrl: string;
    meUrl: string;
}

async function fetchAuthSetup(): Promise<AuthSetup> {
    const response = await fetch(`${BACKEND_URI}/auth_setup`);
    if (!response.ok) {
        throw new Error(`auth setup response was not ok: ${response.status}`);
    }
    return await response.json();
}

const authSetup = await fetchAuthSetup();

export const useLogin = authSetup.useLogin;
export const requireAccessControl = authSetup.requireAccessControl;
export const authMode = authSetup.authMode ?? "bff";
export const loginUrl = authSetup.loginUrl ?? "/auth/login";
export const logoutUrl = authSetup.logoutUrl ?? "/auth/logout";
export const meUrl = authSetup.meUrl ?? "/auth/me";


// Current identity, bootstrapped once from the BFF. Login/logout trigger a full
// page navigation (redirect to /auth/login and back), so this module re-initializes
// and re-fetches /auth/me on each load — no reactive state is required.
let currentAccount: AuthAccount | null = null;

async function fetchMe(): Promise<AuthAccount | null> {
    if (!useLogin) {
        return null;
    }
    try {
        const response = await fetch(`${BACKEND_URI}${meUrl}`, { credentials: "include" });
        if (!response.ok) {
            return null;
        }
        const data = await response.json();
        if (data && data.authenticated) {
            return { idTokenClaims: (data.claims ?? {}) as AuthClaims };
        }
        return null;
    } catch {
        return null;
    }
}

currentAccount = await fetchMe();

// Minimal client shim: existing call sites previously used `useMsal().instance`.
// They now use `authClient`, which exposes only what the app needs.
export const authClient = {
    getActiveAccount: (): AuthAccount | null => currentAccount
};
export type AuthClient = typeof authClient;

export const getActiveAccount = (_client?: AuthClient): AuthAccount | null => currentAccount;

export const isLoggedIn = (_client?: AuthClient): boolean => currentAccount != null;

// Kept for API compatibility with the pre-BFF app-services path; always null now.
export const appServicesToken = null;

// Read the double-submit CSRF token from the non-HttpOnly cookie.
export const getCsrfToken = (): string | undefined => {
    const match = document.cookie.match(/(?:^|;\s*)bb_csrf=([^;]+)/);
    return match ? decodeURIComponent(match[1]) : undefined;
};

// Start the BFF login flow (server-side Authorization Code exchange).
export const login = (): void => {
    window.location.href = `${BACKEND_URI}${loginUrl}`;
};

// Clear the server session + cookies, then sign out of Entra as well.
//
// Clearing the local session alone is not a logout: Entra keeps its own SSO cookie,
// so the auto-login in index.tsx is satisfied silently and the user is signed back
// in without ever seeing a prompt. The backend returns endSessionUrl for this;
// navigating to it clears the Entra session so the next login shows the GM sign-in
// page. Falls back to the app root if the backend did not supply one.
export const logout = async (): Promise<void> => {
    let endSessionUrl: string | undefined;
    try {
        const response = await fetch(`${BACKEND_URI}${logoutUrl}`, {
            method: "POST",
            credentials: "include",
            headers: { "X-CSRF-Token": getCsrfToken() ?? "" }
        });
        if (response.ok) {
            endSessionUrl = (await response.json())?.endSessionUrl ?? undefined;
        }
    } catch {
        // Best-effort; fall through to reload regardless.
    }
    currentAccount = null;
    window.location.href = endSessionUrl ?? "/";
};

// Deprecated under BFF — there is no browser access token. Retained as a no-op
// so existing call sites keep compiling during the transition.
export const getToken = async (_client?: AuthClient): Promise<string | undefined> => {
    return undefined;
};

// GM Customized logic for login and Role based Access Control.
export function useAuthentication() {
    const idTokenClaims: AuthClaims = currentAccount?.idTokenClaims ?? {};
    const idTokenClaimsString = JSON.stringify(idTokenClaims);

    const hasAllRoles = (role: string | string[]): boolean => {
        if (Array.isArray(role)) {
            return role.every(r => hasAllRoles(r));
        }
        return idTokenClaims.roles?.includes(role) ?? false;
    };

    const hasSomeRoles = (role: string | string[]): boolean => {
        if (!idTokenClaims.roles) {
            return false;
        }
        if (Array.isArray(role)) {
            return role.some(r => hasSomeRoles(r));
        }
        return idTokenClaims.roles?.includes(role) ?? false;
    };

    const hasAdminRoleLocal = (): boolean => hasSomeRoles(ADMIN_ROLES);
    const hasDeveloperRoleLocal = (): boolean => hasAllRoles(IT_ROLE);
    const hasNonGpscRole = (): boolean => hasAllRoles(NON_GPSC_USER_ROLE);
    const hasEcrAgentRole = (): boolean => hasAllRoles(ECR_AGENT_USER_ROLE);

    return {
        client: authClient,
        idTokenClaims,
        idTokenClaimsString,
        isAdmin: hasAdminRoleLocal,
        isDomainAdmin: hasAllRoles,
        isUser,
        isNonGpscUser,
        isDeveloper: hasDeveloperRoleLocal,
        hasAllRoles,
        hasSomeRoles,
        hasNonGpscRole,
        hasEcrAgentRole
    };
}

export const isUserNotAuthenticated = (client?: AuthClient): boolean => {
    return useLogin && (!isLoggedIn(client) || !isUser(getActiveAccount(client)));
};

export const checkUri = (uri: string, client?: AuthClient): string => {
    if (isUserNotAuthenticated(client)) {
        return "/";
    } else {
        if (uri == "qa") {
            return "/qa";
        } else if (uri == "admin") {
            return "/admin";
        } else if (uri == "info") {
            return "/info";
        } else {
            return "/chat";
        }
    }
};

export const hasAdminRole = (activeAccount: AuthAccount | null | undefined): boolean => {
    if (!activeAccount) {
        return false;
    }
    const roles = activeAccount.idTokenClaims?.roles ?? [];
    return roles.some((role: string) => ADMIN_ROLES.includes(role));
};

export const hasDeveloperRole = (activeAccount: AuthAccount | null | undefined): boolean => {
    if (!activeAccount) {
        return false;
    }
    const roles = activeAccount.idTokenClaims?.roles ?? [];
    return roles.some((role: string) => role === IT_ROLE);
};

export const isNonGpscUser = (activeAccount: AuthAccount | null | undefined): boolean => {
    if (!activeAccount) {
        return false;
    }
    const roles = activeAccount.idTokenClaims?.roles ?? [];
    return roles.every((role: string) => role === NON_GPSC_USER_ROLE);
};

export const isEcrAgentUser = (activeAccount: AuthAccount | null | undefined): boolean => {
    if (!activeAccount) {
        return false;
    }
    const roles = activeAccount.idTokenClaims?.roles ?? [];
    return roles.some((role: string) => role === ECR_AGENT_USER_ROLE || role === IT_ROLE);
};

// Someone with any of the roles is considered a user
export const isUser = (activeAccount: AuthAccount | null | undefined): boolean => {
    if (!activeAccount) {
        return false;
    }
    const roles = activeAccount.idTokenClaims?.roles ?? [];
    return roles.some((role: string) => ALL_VALID_ROLES.includes(role));
};

export const isSupplyChainUser = (activeAccount: AuthAccount | null | undefined): boolean => {
    if (!activeAccount) {
        return false;
    }
    const roles = activeAccount.idTokenClaims?.roles ?? [];
    return roles.some((role: string) => role === SUPPLYCHAIN_USER_ROLE);
};

export const isDomain4User = (activeAccount: AuthAccount | null | undefined): boolean => {
    if (!activeAccount) {
        return false;
    }
    const roles = activeAccount.idTokenClaims?.roles ?? [];
    return roles.some((role: string) => role === DOMAIN_4_USER_ROLE);
};
